import React, { useState, useEffect } from 'react';
import { Edit, AlertTriangle, Plus, Search, Eye, Trash2, Image, X, ShoppingCart } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import EnhancedPartModal from './EnhancedPartModal';
import PartViewModal from './PartViewModal';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';

// Decode HTML entities in URLs (legacy data may have &#x2F; instead of /)
const decodeImageUrl = (url) => {
  if (!url) return url;
  const txt = document.createElement('textarea');
  txt.innerHTML = url;
  return txt.value;
};

const PartsManagement = ({ onTabChange }) => {
  const { organization } = useOrganization();
  const [parts, setParts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [viewingPart, setViewingPart] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});
  const [selectedParts, setSelectedParts] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [_bulkAction, _setBulkAction] = useState('');
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [selectedPartPhotos, setSelectedPartPhotos] = useState(null);

  useEffect(() => {
    fetchParts();
    fetchVendors();
  }, [filters]);

  const fetchParts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/parts?${params}`);
      const partsData = Array.isArray(response.data.parts) ? response.data.parts : Array.isArray(response.data) ? response.data : [];
      setParts(partsData);
      setPagination(response.data.pagination || {});
    } catch (error) {
      toast.error('Failed to fetch parts');
      setParts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data.vendors || response.data);
    } catch (error) {
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setShowModal(true);
  };

  const handleView = (part) => {
    setViewingPart(part);
  };

  const handleViewPhotos = (part) => {
    setSelectedPartPhotos(part);
    setShowPhotosModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        await axios.delete(`/api/parts/${id}`);
        toast.success('Part deleted successfully');
        fetchParts();
      } catch (error) {
        toast.error('Failed to delete part');
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingPart(null);
    fetchParts();
  };

  const handleSelectPart = (partId) => {
    setSelectedParts(prev => 
      prev.includes(partId) 
        ? prev.filter(id => id !== partId)
        : [...prev, partId]
    );
  };

  const handleSelectAll = () => {
    if (selectedParts.length === parts.length) {
      setSelectedParts([]);
    } else {
      setSelectedParts(parts.map(part => part.id));
    }
  };

  const handleCreatePurchaseOrder = () => {
    if (selectedParts.length === 0) {
      toast.error('Please select parts to create purchase order');
      return;
    }
    
    // Store selected parts in localStorage for the purchase order form
    const selectedPartsData = parts.filter(part => selectedParts.includes(part.id));
    localStorage.setItem('selectedPartsForPO', JSON.stringify(selectedPartsData));
    
    // Switch to purchase orders tab
    if (onTabChange) {
      onTabChange('purchase-orders');
      toast.success(`${selectedParts.length} parts selected for purchase order`);
    }
  };

  const handleCreateSinglePO = (part) => {
    // Store single part in localStorage for the purchase order form
    localStorage.setItem('selectedPartsForPO', JSON.stringify([part]));
    
    // Switch to purchase orders tab
    if (onTabChange) {
      onTabChange('purchase-orders');
      toast.success(`${part.name} selected for purchase order`);
    }
  };

  const handleBulkAction = async (action, data) => {
    try {
      await axios.post('/api/parts/bulk-update', {
        action,
        partIds: selectedParts,
        updateData: data
      });
      toast.success(`Bulk ${action} completed successfully`);
      setSelectedParts([]);
      setShowBulkModal(false);
      fetchParts();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const getStockStatus = (quantity, minStock = 10) => {
    if (quantity === 0) return { status: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    if (quantity <= minStock) return { status: 'Low Stock', color: 'text-yellow-600 bg-yellow-100' };
    return { status: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900">Parts Management</h2>
          {selectedParts.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{selectedParts.length} selected</span>
              <button
                onClick={() => handleCreatePurchaseOrder()}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                Create PO
              </button>
              <button
                onClick={() => setShowBulkModal(true)}
                className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
              >
                Bulk Actions
              </button>
            </div>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => onTabChange && onTabChange('purchase-orders')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Purchase Orders
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Part
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search parts..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <input
            type="text"
            placeholder="Search by group..."
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Parts Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedParts.length === parts.length && parts.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Part Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand/Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan="7" className="px-6 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : parts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No parts found
                  </td>
                </tr>
              ) : (
                parts.map((part) => {
                  const stockStatus = getStockStatus(part.quantity, part.minStock);
                  
                  return (
                    <tr key={part.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedParts.includes(part.id)}
                          onChange={() => handleSelectPart(part.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {part.imageUrl && (
                            <img
                              src={decodeImageUrl(part.imageUrl)}
                              alt={part.name}
                              className="w-10 h-10 object-cover rounded-md mr-3"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{part.name}</div>
                            <div className="text-sm text-gray-500">{part.sku || 'No SKU'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div>{part.vendor?.name || 'No Vendor'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div>{part.category || 'No Category'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900 mr-2">
                            {part.quantity} pcs
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${stockStatus.color}`}>
                            {stockStatus.status}
                          </span>
                          {part.quantity <= (part.minStock || 10) && (
                            <AlertTriangle className="w-4 h-4 text-yellow-500 ml-2" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div>Cost: {formatCurrency(part.costPrice || 0, organization?.currency_code)}</div>
                          <div>Sell: {formatCurrency(part.price || 0, organization?.currency_code)}</div>
                          {(part.b2bMargin || part.b2cMargin) && (
                            <div className="text-xs text-gray-500">
                              B2B: {part.b2bMargin}% | B2C: {part.b2cMargin}%
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            Stock
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleView(part)}
                            className="text-gray-600 hover:text-gray-900"
                            title="View"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => handleViewPhotos(part)}
                            className="text-purple-600 hover:text-purple-900"
                            title="View Photos"
                          >
                            <Image className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCreateSinglePO(part)}
                            className="text-green-600 hover:text-green-900"
                            title="Create Purchase Order"
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(part)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(part.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.currentPage === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Part Modal */}
      {showModal && (
        <EnhancedPartModal
          part={editingPart}
          vendors={vendors}
          onClose={handleModalClose}
        />
      )}

      {/* View Modal */}
      {viewingPart && (
        <PartViewModal
          part={viewingPart}
          onClose={() => setViewingPart(null)}
        />
      )}

      {/* Bulk Actions Modal */}
      {showBulkModal && (
        <BulkActionsModal
          selectedCount={selectedParts.length}
          onClose={() => setShowBulkModal(false)}
          onAction={handleBulkAction}
        />
      )}

      {/* Photos Modal */}
      {showPhotosModal && selectedPartPhotos && (
        <PhotosModal
          part={selectedPartPhotos}
          onClose={() => setShowPhotosModal(false)}
        />
      )}
    </div>
  );
};

// Bulk Actions Modal Component
const BulkActionsModal = ({ selectedCount, onClose, onAction }) => {
  const [action, setAction] = useState('');
  const [formData, setFormData] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onAction(action, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">Bulk Actions ({selectedCount} items)</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Action</label>
            <select 
              value={action} 
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Action</option>
              <option value="updateStatus">Update Status</option>
              <option value="updateCategory">Update Category</option>
              <option value="updateStock">Update Stock Levels</option>
              <option value="updateB2BMargin">Update B2B Margin (%)</option>
              <option value="updateB2CMargin">Update B2C Margin (%)</option>
              <option value="delete">Delete Parts</option>
            </select>
          </div>

          {action === 'updateStatus' && (
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select 
                onChange={(e) => setFormData({status: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DISCONTINUED">Discontinued</option>
              </select>
            </div>
          )}

          {action === 'updateCategory' && (
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select 
                onChange={(e) => setFormData({category: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Category</option>
                <option value="Engine">Engine</option>
                <option value="Brakes">Brakes</option>
                <option value="Electrical">Electrical</option>
                <option value="Suspension">Suspension</option>
                <option value="Transmission">Transmission</option>
                <option value="AC">AC</option>
                <option value="Tires">Tires</option>
              </select>
            </div>
          )}

          {action === 'updateStock' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Min Stock</label>
                <input 
                  type="number" 
                  onChange={(e) => setFormData(prev => ({...prev, minStock: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Minimum stock level"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Stock</label>
                <input 
                  type="number" 
                  onChange={(e) => setFormData(prev => ({...prev, maxStock: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Maximum stock level"
                />
              </div>
            </div>
          )}

          {action === 'updateB2BMargin' && (
            <div>
              <label className="block text-sm font-medium mb-2">B2B Margin (%)</label>
              <input 
                type="number" 
                step="0.01"
                min="0"
                max="100"
                onChange={(e) => setFormData({b2bMargin: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Enter B2B margin percentage"
                required
              />
            </div>
          )}

          {action === 'updateB2CMargin' && (
            <div>
              <label className="block text-sm font-medium mb-2">B2C Margin (%)</label>
              <input 
                type="number" 
                step="0.01"
                min="0"
                max="100"
                onChange={(e) => setFormData({b2cMargin: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Enter B2C margin percentage"
                required
              />
            </div>
          )}

          {action === 'delete' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                ⚠️ This will permanently delete {selectedCount} parts. This action cannot be undone.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className={`px-4 py-2 rounded-md text-white ${
                action === 'delete' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {action === 'delete' ? 'Delete' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Photos Modal Component
const PhotosModal = ({ part, onClose }) => {
  const allImages = [];
  
  // Add main image if exists
  if (part.imageUrl) allImages.push(decodeImageUrl(part.imageUrl));
  
  // Add additional images if exists
  if (part.images && Array.isArray(part.images)) {
    part.images.forEach(img => {
      const decoded = decodeImageUrl(img);
      if (decoded && decoded.trim() !== '' && !allImages.includes(decoded)) {
        allImages.push(decoded);
      }
    });
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Photos - {part.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {allImages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No photos available for this part</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {allImages.map((image, index) => (
                <div key={index} className="aspect-square">
                  <img 
                    src={image} 
                    alt={`${part.name} - Photo ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => window.open(image, '_blank')}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartsManagement;
